import * as React from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Loader2, Loader, Clock } from "lucide-react";
import { TbWindowMaximize, TbWindowMinimize, TbFileExport } from "react-icons/tb";
import { FiEdit2 } from "react-icons/fi";
import { FaRegTrashCan } from "react-icons/fa6";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { ICompasAIChatAction } from "../../types/compass.types";
import ReactMarkdown from "react-markdown";
import { useEditActionPrompt, useInvalidateCompassChat } from "../../hooks/useCompassChat";
import { useRunCompassActions } from "../../hooks/useCompassActions";
import { useCancelQueuedAction, useDeleteAction } from "../../hooks/useCompassQueue";
import { useDocument } from "../../hooks/useDocument";
import CreateRequestDialog from "./request-gallery/CreateRequestDialog";
import { useCompassSocketContext } from "@/context/CompassSocketContext";
import { toast } from "sonner";

interface ActionItemProps {
  action: ICompasAIChatAction;

  isExpanded: boolean;
  toggleExpansion: () => void;
  actionState: string;
  isGlobalEditMode: boolean;
}

interface ActionItemActionsProps {
  action: ICompasAIChatAction;

  isExpanded: boolean;
  toggleExpansion: () => void;

  handleToggleEditing: () => void;
  handleDeleteAction: () => void;
  handleSaveToLibrary: () => void;
  isSavingToLibrary: boolean;
  currentStatus: string;
}

const ActionItemActions: React.FC<ActionItemActionsProps> = ({
  isExpanded,
  toggleExpansion,

  handleToggleEditing,
  handleDeleteAction,
  handleSaveToLibrary,
  isSavingToLibrary,
  currentStatus,
}) => {
  const iconColor = currentStatus === "processing" ? "text-white" : "text-neutral-400";

  const actions = [
    {
      id: "expand-toggle",
      type: "button",
      icon: isExpanded ? (
        <TbWindowMinimize className={`size-3.5 ${iconColor}`} />
      ) : (
        <TbWindowMaximize className={`size-3.5 ${iconColor}`} />
      ),
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        toggleExpansion();
      },
      tooltipContent: isExpanded ? "Minimize" : "Maximize",
    },
    {
      id: "save",
      type: "button",
      icon: <TbFileExport className={`size-3.5 ${iconColor}`} />,
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        handleSaveToLibrary();
      },
      tooltipContent: "Save to request library",
      disabled: isSavingToLibrary,
    },
    {
      id: "edit",
      type: "button",
      icon: <FiEdit2 className={`size-3.5 ${iconColor}`} />,
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        handleToggleEditing();
      },
      tooltipContent: "Edit Action",
    },
    {
      id: "delete",
      type: "button",
      icon: <FaRegTrashCan className="size-3.5 text-danger-200" />,
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        handleDeleteAction();
      },
      tooltipContent: "Remove action",
    },
  ].filter(Boolean);

  return (
    <div className="flex flex-col items-center gap-3">
      {actions.map((act: any) => (
        <TooltipProvider key={act.id}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                onClick={(e) => {
                  if (act.disabled) {
                    e.stopPropagation();
                    return;
                  }
                  act.onClick(e);
                }}
                className={act.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
              >
                {act.icon}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{act.tooltipContent}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}
    </div>
  );
};

const ActionItem: React.FC<ActionItemProps> = ({
  action,

  isExpanded,
  toggleExpansion,
  actionState,
  isGlobalEditMode,
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editedPrompt, setEditedPrompt] = React.useState(action.prompt);

  React.useEffect(() => {
    setIsEditing(isGlobalEditMode);
  }, [isGlobalEditMode]);

  const { data: doc } = useDocument();
  const editActionMutation = useEditActionPrompt();
  const deleteActionMutation = useDeleteAction();
  const runActionsMutation = useRunCompassActions();
  useCancelQueuedAction();
  const invalidateCompassChat = useInvalidateCompassChat();
  const [isCreateRequestOpen, setIsCreateRequestOpen] = React.useState(false);
  const [createRequestData, setCreateRequestData] = React.useState<
    { name: string; content: string } | undefined
  >(undefined);

  const { taskStatuses } = useCompassSocketContext();
  const statusKey = action.queue_item_id || action.id;
  const currentStatus = taskStatuses[statusKey] || actionState;

  const handleSaveToLibrary = () => {
    setCreateRequestData({
      name: action.heading || action.label || "", // Name will be generated in the dialog if empty
      content: action.prompt,
    });
    setIsCreateRequestOpen(true);
  };

  const getActionCardClass = (state: string): string => {
    let baseClass = "py-3 px-4 rounded-lg text-sm cursor-pointer relative";

    if (state === "queued" || state === "pending")
      return `${baseClass} bg-warning-100 border border-transparent text-neutral-200 shadow-lg hover:border-warning-200 hover:bg-warning-100-hover`;
    if (state === "processing")
      return `${baseClass} bg-primary-300 border-primary-300 text-white shadow-lg`;
    if (state === "processed" || state === "completed")
      return `${baseClass} bg-success-200 border-success-200 text-neutral-white shadow-lg`;
    if (state === "failed")
      return `${baseClass} bg-danger-200 border-danger-200 text-white shadow-lg`;

    return `${baseClass} bg-compass-bg text-compass-text border border-transparent hover:border-compass-text hover:bg-compass-bg-hover`;
  };

  const getMarkdownColorClass = (state: string): string => {
    if (state === "queued" || state === "pending") return "text-neutral-200";
    if (state === "processing") return "text-white";
    if (state === "processed" || state === "completed") return "text-neutral-white";
    if (state === "failed") return "text-white";
    return "text-compass-text";
  };

  const handleToggleEditing = () => {
    if (isEditing) {
      if (editedPrompt && editedPrompt !== action.prompt && doc?._id) {
        editActionMutation.mutate(
          {
            actionId: action.id,
            contextId: doc._id,
            prompt: editedPrompt,
          },
          {
            onSuccess: () => {
              setIsEditing(false);
              invalidateCompassChat(doc._id);
            },
          }
        );
      } else {
        setIsEditing(false);
      }
    } else {
      setIsEditing(true);
    }
  };

  const handleCancelEditing = () => {
    setEditedPrompt(action.prompt);
    setIsEditing(false);
  };

  const handleDeleteAction = () => {
    if (!doc?._id) return;

    deleteActionMutation.mutate({
      actionId: action.id,
      contextId: doc._id,
    });
  };

  const executeAction = async () => {
    if (!doc?._id || !doc?.title) return;

    try {
      const actionPrompt = editedPrompt;

      toast.success("Compass started processing action", { duration: 2000 });

      const actionRequest = {
        id: action.id,
        prompt: actionPrompt,
        module: action.module || "",
        feature: action.feature || "",
        current_document_id: doc._id,
        document_ids: [doc._id],
        document_title: [doc.title],
      };

      runActionsMutation.mutate(
        {
          requests: [actionRequest],
          documentId: doc._id,
        },
        {
          onSuccess: () => {
            if (doc?._id) {
              invalidateCompassChat(doc._id);
            }
          },
          onError: (error) => {
            console.error("Failed to execute action:", error);
          },
        }
      );
    } catch (error) {
      console.error("Failed to execute action:", error);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`${getActionCardClass(currentStatus)} flex items-start gap-2`}
            onClick={() => {
              if (!isEditing) {
                if (currentStatus === "normal") {
                  executeAction();
                } else if (currentStatus === "processed" || currentStatus === "completed") {
                  executeAction();
                }
              }
            }}
          >
            <div className="grow flex flex-col gap-2">
              <div className="flex items-center gap-2">
                {(currentStatus === "queued" || currentStatus === "pending") && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Clock className="size-3.5 text-neutral-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>In queue</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {currentStatus === "processing" && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Loader className="size-3.5 animate-spin text-white" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Processing...</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {(currentStatus === "processed" || currentStatus === "completed") && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <IoMdCheckmarkCircleOutline className="size-3.5 text-white" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Completed</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                <h6 className="text-sm font-semibold">
                  {action.heading || action.label || "Action Item"}
                </h6>
              </div>
              {isEditing ? (
                <>
                  <Textarea
                    value={editedPrompt}
                    onChange={(e) => setEditedPrompt(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-neutral-white text-neutral-400"
                    rows={5}
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelEditing();
                      }}
                      className="text-neutral-400"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      variant="default"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleEditing();
                      }}
                      disabled={editActionMutation.isPending}
                    >
                      {editActionMutation.isPending ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    wordBreak: "break-word",
                  }}
                  className={`prose prose-sm max-w-none ${getMarkdownColorClass(currentStatus)}`}
                >
                  <ReactMarkdown
                    components={{
                      a: ({ node, ...props }) => (
                        <a {...props} target="_blank" rel="noopener noreferrer" />
                      ),
                    }}
                  >
                    {isExpanded
                      ? action.prompt
                      : action.prompt && action.prompt.length > 150
                        ? action.prompt.substring(0, 150) + "..."
                        : action.prompt}
                  </ReactMarkdown>
                </div>
              )}
            </div>

            {!isEditing && (
              <div className="flex items-start gap-2">
                <React.Suspense fallback={<Loader className="animate-spin" />}>
                  <ActionItemActions
                    action={action}
                    isExpanded={isExpanded}
                    toggleExpansion={toggleExpansion}
                    handleToggleEditing={handleToggleEditing}
                    handleDeleteAction={handleDeleteAction}
                    handleSaveToLibrary={handleSaveToLibrary}
                    isSavingToLibrary={false}
                    currentStatus={currentStatus}
                  />
                </React.Suspense>
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-[260px]">
          <p>
            {currentStatus === "processed" || currentStatus === "completed"
              ? "Click to re-run the action"
              : 'Click to initiate the action or click "Edit" button to modify'}
          </p>
        </TooltipContent>
      </Tooltip>
      <CreateRequestDialog
        open={isCreateRequestOpen}
        onOpenChange={setIsCreateRequestOpen}
        initialData={createRequestData}
        type="discuss"
      />
    </TooltipProvider>
  );
};

export default ActionItem;
